import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import { sanitizeRedirect } from "./redirect";
import { type LogoutReason, useAuthStore } from "./store";

/**
 * IR-1241 — the reactive half of "automatic full logout".
 *
 * The /_authed route guard only re-evaluates auth on navigation/reload, so
 * before this existed, an in-session token expiry (api.ts calling
 * useAuthStore.clear()) left the operator stuck on a stale screen showing
 * per-widget errors ("failed to load links") until a manual refresh. This
 * subscribes to the store and, the moment the user transitions from
 * authenticated -> null (for ANY reason: expired refresh, idle timeout, or
 * manual sign-out), clears the React-Query cache (so stale widgets stop
 * re-erroring) and redirects to /login carrying the reason for the banner.
 */
export function useSessionRedirect(
  navigate: (reason: LogoutReason, redirectTo?: string) => void,
  clearCache: () => void,
  /**
   * IR-1288 — the page on screen at the instant the session died, read lazily
   * so it reflects the live location rather than whatever was current when the
   * subscription was set up. Held in a ref so a changing identity never
   * re-subscribes the store listener.
   */
  currentHref: () => string | undefined = () => undefined,
): void {
  const hrefRef = useRef(currentHref);
  hrefRef.current = currentHref;

  useEffect(() => {
    return useAuthStore.subscribe((state, prev) => {
      if (prev.user && !state.user) {
        clearCache();
        const reason = state.logoutReason ?? "expired";
        // An involuntary logout restores where the operator was; a deliberate
        // Sign-out does not — they meant to leave, so they land on Overview.
        const back = reason === "manual" ? undefined : sanitizeRedirect(hrefRef.current());
        navigate(reason, back);
      }
    });
  }, [navigate, clearCache]);
}

export function SessionController(): null {
  const nav = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  useSessionRedirect(
    useCallback(
      (reason: LogoutReason, redirectTo?: string) => {
        // `exactOptionalPropertyTypes` — omit the key rather than pass undefined.
        nav({
          to: "/login",
          search: redirectTo ? { reason, redirect: redirectTo } : { reason },
          replace: true,
        });
      },
      [nav],
    ),
    useCallback(() => queryClient.clear(), [queryClient]),
    useCallback(() => router.state.location.href, [router]),
  );

  return null;
}
